// ============================================================
// Bekleme listesi ve kontenjan projeksiyonu (§10)
//
// Başvuru hunisi çalışıyordu ama sınıf dolduğunda ne olacağı
// tanımsızdı. Üç şey ekleniyor:
//
//   1. Dolu sınıfa gelen başvuru bekleme listesine düşer, sıra alır
//   2. Sıralama kardeş önceliğini gözetir — `siblingId` zaten vardı
//   3. Eylül'de bir üst yaş grubuna geçecek çocuklar hesaba katılarak
//      GELECEK DÖNEM kontenjanı öngörülür
//
// Üçüncüsü kritik: bugün dolu görünen sınıf, Eylül'de büyükler
// ilkokula geçince açılır. Bunu görmeden bekleme listesi yönetilemez.
// ============================================================

import { BUGUN } from "../config.js";
import { B, subeBulHerYerde } from "./context.js";
import { kontenjan } from "../views/basvurular.js";

/** Yaş grubu geçiş ayı — KKTC'de eğitim yılı Eylül'de başlar. */
export const DONEM_BASI = "09";

/**
 * İlkokula başlama eşiği (ay).
 *
 * KKTC ve Türkiye uygulamasında eylül sonunda 69 ayını dolduran çocuk
 * ilkokula başlar. Yıl bazlı hesap ("6 yaşını doldurdu mu") yanlış
 * sonuç verir: Ekim doğumlu bir çocuk eylülde 5 yaş 10 ay olur ama
 * ilkokula başlar. Bu yüzden tüm geçiş hesabı AY üzerinden yapılır.
 *
 * Şube ayarından değiştirilebilir olmalı — mevzuat değişebilir.
 */
export const ILKOKUL_ESIGI_AY = 69;

const gunFark = (a, b) =>
  Math.round((new Date(a + "T00:00:00Z") - new Date(b + "T00:00:00Z")) / 86400000);

const sube = branchId => (branchId ? subeBulHerYerde(branchId) : B());

// ---------- Bekleme listesi ----------

/**
 * Bir sınıfın bekleme listesi.
 * Sıralama: kardeş önceliği → başvuru tarihi.
 * Kardeş önceliği ticari değil operasyonel bir tercih: iki çocuğu
 * farklı kurumda olan aile ikisini de alır götürür.
 */
export function beklemeListesi(classId, branchId) {
  const t = sube(branchId);
  const k = kontenjanHesapla(classId, branchId);
  if (!k) return [];

  const adaylar = t.applications
    .filter(a => a.targetClassId === classId)
    .filter(a => !["kayit", "kaybedildi"].includes(a.stage));

  // Kontenjan varsa bekleme listesi yok — herkes normal huniye girer.
  if (k.bos > 0) return [];

  return adaylar
    .map(a => ({
      basvuru: a,
      kardes: Boolean(a.siblingId),
      bekleyenGun: gunFark(BUGUN, a.createdAt),
    }))
    .sort((x, y) => {
      if (x.kardes !== y.kardes) return x.kardes ? -1 : 1;
      return x.basvuru.createdAt.localeCompare(y.basvuru.createdAt);
    })
    .map((x, i) => ({ ...x, sira: i + 1 }));
}

/** Şubedeki tüm bekleme listeleri. */
export function tumBeklemeListeleri(branchId) {
  const t = sube(branchId);
  return t.classes
    .map(c => ({ sinif: c, liste: beklemeListesi(c.id, branchId) }))
    .filter(x => x.liste.length);
}

// ---------- Kontenjan ----------

export function kontenjanHesapla(classId, branchId) {
  const t = sube(branchId);
  const c = t.classes.find(x => x.id === classId);
  if (!c) return null;

  const dolu = t.students.filter(s => s.classId === classId).length;
  return { c, dolu, kapasite: c.capacity, bos: c.capacity - dolu };
}

/**
 * Gelecek dönem kontenjan projeksiyonu.
 *
 * Çocuklar Eylül'de bir üst yaş grubuna geçer; en büyük gruptakiler
 * ilkokula gider ve kurumdan ayrılır. Bugün dolu görünen sınıf o zaman
 * açılabilir — bekleme listesindeki aileye "Eylül'de yeriniz var"
 * diyebilmek için bu hesap gerekiyor.
 */
export function donemProjeksiyonu(branchId, period) {
  const t = sube(branchId);
  const hedefDonem = period ?? gelecekDonem();
  const yil = Number(hedefDonem.slice(0, 4));

  // Sınıflar yaş sırasına göre.
  const sirali = [...t.classes].sort((a, b) => yasBandi(a).altAy - yasBandi(b).altAy);

  /** Bu sınıftan ayrılacaklar: bandını aşanlar ya da ilkokula gidenler. */
  const ayrilanlar = (c, enUst) => {
    const band = yasBandi(c);
    return t.students.filter(s => s.classId === c.id).filter(s => {
      const ay = yasAyEylulde(s, yil);
      return enUst ? ay >= ILKOKUL_ESIGI_AY : ay > band.ustAy;
    });
  };

  const projeksiyon = sirali.map((c, i) => {
    const mevcut = t.students.filter(s => s.classId === c.id);
    const enUst = i === sirali.length - 1;

    const gidecek = ayrilanlar(c, enUst);
    const altSinif = sirali[i - 1] ?? null;
    const gelecek = altSinif ? ayrilanlar(altSinif, false) : [];

    const kalan = mevcut.length - gidecek.length + gelecek.length;

    return {
      sinif: c,
      bugunDolu: mevcut.length,
      gidecek: gidecek.length,
      gidecekler: gidecek,
      gelecek: gelecek.length,
      // En büyük gruptakiler ilkokula gider; üst sınıf yok.
      mezun: enUst ? gidecek.length : 0,
      projeksiyonDolu: kalan,
      projeksiyonBos: c.capacity - kalan,
      kapasite: c.capacity,
    };
  });

  return { donem: hedefDonem, siniflar: projeksiyon };
}

/** Bekleme listesindekilere yer açılacak mı? */
export function acilacakYerler(branchId) {
  const p = donemProjeksiyonu(branchId);
  return p.siniflar
    .filter(x => x.projeksiyonBos > 0)
    .map(x => ({
      ...x,
      bekleyen: beklemeListesi(x.sinif.id, branchId).length,
      // Yer açılıyor ve bekleyen var → haber verilmeli.
      haberVerilecek: Math.min(x.projeksiyonBos, beklemeListesi(x.sinif.id, branchId).length),
    }))
    .filter(x => x.bekleyen > 0);
}

// ---------- Peşinat ----------

export const pesinatOzeti = (branchId) => {
  const t = sube(branchId);
  const kayitlar = t.applications.filter(a => a.stage === "kayit");
  const pesinatli = kayitlar.filter(a => a.deposit?.paidAt);

  return {
    kayit: kayitlar.length,
    pesinatli: pesinatli.length,
    bekleyen: kayitlar.length - pesinatli.length,
    toplam: pesinatli.reduce((a, x) => a + (x.deposit?.amount ?? 0), 0),
  };
};

// ---------- Özet ----------

export function beklemeOzeti(branchId) {
  const listeler = tumBeklemeListeleri(branchId);
  const acilacak = acilacakYerler(branchId);

  return {
    doluSinif: listeler.length,
    bekleyenToplam: listeler.reduce((a, x) => a + x.liste.length, 0),
    kardesli: listeler.reduce((a, x) => a + x.liste.filter(y => y.kardes).length, 0),
    acilacakYer: acilacak.reduce((a, x) => a + x.projeksiyonBos, 0),
    haberVerilecek: acilacak.reduce((a, x) => a + x.haberVerilecek, 0),
    pesinat: pesinatOzeti(branchId),
  };
}

// ---------- Yardımcılar ----------

/**
 * Sınıfın yaş bandı, AY cinsinden. "4–5 yaş" → 48–59 ay.
 * Bantlar tam 12 ay genişliğindedir; çocuk üst sınıra ulaşınca
 * bir üst gruba geçer.
 */
export const yasBandi = c => {
  const alt = Number((c.age.match(/(\d+)/) ?? [0, 0])[1]);
  return { altAy: alt * 12, ustAy: alt * 12 + 11 };
};

/** Çocuğun hedef yılın 1 Eylül'ündeki yaşı — AY cinsinden. */
export function yasAyEylulde(student, yil) {
  const d = new Date(student.birthDate + "T00:00:00Z");
  const eylul = new Date(`${yil}-${DONEM_BASI}-01T00:00:00Z`);
  const ay = (eylul.getUTCFullYear() - d.getUTCFullYear()) * 12
           + (eylul.getUTCMonth() - d.getUTCMonth());
  return eylul.getUTCDate() < d.getUTCDate() ? ay - 1 : ay;
}

export function gelecekDonem(bugun = BUGUN) {
  const yil = Number(bugun.slice(0, 4));
  const ay = bugun.slice(5, 7);
  // Eylül'den önceyse bu yılın Eylül'ü, sonraysa gelecek yılınki.
  return ay < DONEM_BASI ? `${yil}-${DONEM_BASI}` : `${yil + 1}-${DONEM_BASI}`;
}

export { kontenjan };
